import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What is the primary role of nutrition in your body?",
    options: [
      "To provide energy for daily activities",
      "To support growth, repair, and energy production",
      "To make you feel full after eating",
      "To satisfy cravings for sweets"
    ],
    correctAnswer: 1,
    explanation: "Nutrition supports essential biological processes: growth, repair, and energy production. Your body uses nutrients to maintain health, heal injuries, and perform daily functions."
  },
  {
    question: "According to the module, what does 'Food is fuel' mean?",
    options: [
      "Food should only be eaten when you're hungry for gas",
      "Carbohydrates are gasoline, proteins are engine oil, and vitamins are coolant",
      "You should eat less to save fuel costs",
      "Food provides fuel for your car, not your body"
    ],
    correctAnswer: 1,
    explanation: "The analogy helps understand nutrition: carbohydrates (Go foods like rice) provide quick energy, proteins (Grow foods like fish) maintain your 'engine,' and vitamins/minerals (Glow foods like fruits) keep everything running smoothly."
  },
  {
    question: "What is the main benefit of choosing balance over perfection in nutrition?",
    options: [
      "You can eat whatever you want without consequences",
      "Occasional treats won't derail your health journey",
      "You never need to worry about what you eat",
      "Perfection is impossible anyway"
    ],
    correctAnswer: 1,
    explanation: "Balance recognizes that occasional treats won't undo consistent healthy eating. This approach is sustainable and prevents the stress of trying to be perfect, which often leads to giving up entirely."
  },
  {
    question: "The 'Energy Audit' activity helps you identify:",
    options: [
      "How much money you spend on food",
      "Your personal dietary patterns and their impact on well-being",
      "Which foods give you the most calories",
      "How to calculate your daily energy needs"
    ],
    correctAnswer: 1,
    explanation: "The Energy Audit makes nutrition personal by having you circle energizing foods (like eggs, fish, vegetables) and box sluggish foods (like heavy sweets). This reveals your current eating patterns and their effects on your energy levels."
  },
  {
    question: "What does the phrase 'What you eat today becomes your strength tomorrow' mean?",
    options: [
      "Food only affects you immediately",
      "Nutrition has long-term effects on your health and vitality",
      "You should only eat foods that make you strong right away",
      "Tomorrow's strength depends on today's exercise, not food"
    ],
    correctAnswer: 1,
    explanation: "Good nutrition today builds your body's strength for tomorrow. The nutrients you consume become the building blocks for your tissues, energy reserves, and overall health in the future."
  }
];

const NutritionalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('nutritionalM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>How nutrition supports your body's essential functions</span>
              </li>
              <li>
                <span className="check"></span>
                <span>The importance of balance over perfection in eating</span>
              </li>
              <li>
                <span className="check"></span>
                <span>How to assess your personal dietary patterns</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue building your nutritional knowledge!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Nutritional Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Nutritional Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about nutritional wellness.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

                {currentQuestion < quizQuestions.length - 1 ? (
                  <button
                    className="btn-primary"
                    onClick={handleNext}
                    disabled={selectedAnswer === null}
                  >
                    Next Question
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={handleNext}
                    disabled={selectedAnswer === null}
                  >
                    Complete Quiz
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule1QuizPage;

